import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { useCurrency } from '../../hooks/useCurrency';
import { toast } from 'react-hot-toast';
import { openRazorpayCheckout } from '../../utils/razorpayCheckout';
import {
  CreditCard,
  CheckCircle,
  Clock,
  AlertTriangle,
  ArrowRight,
  Shield,
  Edit,
  HelpCircle
} from 'lucide-react';
import { BILLING_INTERVALS, getIntervalMonths } from '../../utils/pricing';

const Billing = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const api = useApi();
  const [billingCurrency, setBillingCurrency] = useState(null);
  const { symbol, getPrice } = useCurrency({ billingCurrency });
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  // Billing term key from utils/pricing (monthly | quarterly | halfyearly | yearly).
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [organization, setOrganization] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [planConfirmed, setPlanConfirmed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [couponCode, setCouponCode] = useState(''); // Coupon code for checkout

  // Both gateways now carry all four terms, so no gateway-specific filtering.
  const intervalOptions = BILLING_INTERVALS;

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    try {
      const response = await api.get('/api/v1/billing/profile');
      setOrganization(response.data.data);
      setBillingCurrency(response.data.data.orgPlanCurrency || (response.data.data.paymentGateway === 'razorpay' ? 'INR' : 'USD'));

      // Calculate trial status - check both organization trialEnd and user trialEndDate
      const now = new Date();
      let trialEnd = null;
      
      // For free plan users, check user's trialEndDate first
      if (response.data.data.orgPlanSelection === 'free' && response.data.data.userTrialEndDate) {
        trialEnd = new Date(response.data.data.userTrialEndDate);
      } else if (response.data.data.trialEnd) {
        // Otherwise check organization trialEnd
        trialEnd = new Date(response.data.data.trialEnd);
      }
      
      if (trialEnd) {
        const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
        
        setTrialStatus({
          expired: trialEnd < now,
          daysRemaining: Math.max(0, daysRemaining),
          trialEnd: trialEnd.toISOString(),
          isFreePlan: response.data.data.orgPlanSelection === 'free'
        });
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
    }
  };

  const plans = [
    {
      id: 'growth',
      name: 'Growth',
      price: getPrice('standard', billingInterval),
      description: 'Ideal for growing teams',
      popular: true,
      features: [
        'Up to 10 team members',
        'Basic AI insights',
        'Email support',
        'Basic analytics dashboard',
        'Mobile app access'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: getPrice('premium', billingInterval),
      description: 'For large organizations',
      features: [
        'Up to 10 team members',
        'Basic AI insights',
        'Email support',
        'Basic analytics dashboard',
        'Mobile app access'
      ]
    }
  ];

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setPlanConfirmed(false);
  };

  const handleChangePlan = () => {
    if (!selectedPlan) {
      toast.error('Please select a plan first');
      return;
    }
    setPlanConfirmed(true);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setLoading(true);
    try {
      // Check if user is on Razorpay gateway
      const isRazorpay = organization?.paymentGateway === 'razorpay';

      if (isRazorpay) {
        const rzpRes = await api.post('/api/v1/razorpay-billing/create-order', {
          plan: selectedPlan.id,
          interval: billingInterval,
          ...(couponCode && { couponCode: couponCode.trim() })
        });
        if (rzpRes.data?.success && (rzpRes.data?.data?.subscriptionId || rzpRes.data?.data?.orderId)) {
          const rzpData = rzpRes.data.data;
          openRazorpayCheckout({
            razorpayKeyId: rzpData.razorpayKeyId,
            amount: rzpData.amount,
            currency: rzpData.currency,
            orderId: rzpData.orderId,
            subscriptionId: rzpData.subscriptionId,
            customerName: user?.firstName + ' ' + user?.lastName,
            customerEmail: user?.email,
            notes: rzpData.notes,
            description: `TeamGrid ${selectedPlan.name} Plan`,
            onSuccess: async (pr) => {
              try {
                await api.post('/api/v1/razorpay-billing/verify-payment', {
                  razorpay_order_id: pr.razorpay_order_id,
                  razorpay_payment_id: pr.razorpay_payment_id,
                  razorpay_signature: pr.razorpay_signature,
                  razorpay_subscription_id: pr.razorpay_subscription_id,
                  plan: selectedPlan.id,
                  totalSeats: rzpData.totalSeats || rzpData.seatCount,
                  type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'billing_checkout',
                  interval: billingInterval,
                });
                toast.success('Payment successful!');
                setTimeout(() => { window.location.href = '/dashboard?payment=success'; }, 100);
              } catch { toast.error('Payment verification failed'); }
            },
            onFailure: () => toast.error('Payment failed'),
            onDismiss: () => setLoading(false),
          });
          return;
        }
      }

      // Stripe flow — the server normalizes 'monthly'/'month' alike.
      const response = await api.post('/api/v1/billing/checkout-session', {
        plan: selectedPlan.id,
        interval: billingInterval,
        ...(couponCode && { couponCode: couponCode.trim() })
      });

      if (response.data.success) {
        window.location.href = response.data.data.sessionUrl;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error.response?.data?.message || 'Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleTooltip = (planId) => {
    setShowTooltip(showTooltip === planId ? null : planId);
  };

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold">TeamGrid</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-300">Welcome, {user?.firstName}</span>
              <button
                onClick={handleLogout}
                className="text-gray-300 hover:text-white"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Trial Status Banner */}
        {trialStatus && (
          <div className="mb-8">
            {trialStatus.expired ? (
              <div className="bg-red-600 border border-red-500 rounded-lg p-6">
                <div className="flex items-center">
                  <AlertTriangle className="h-6 w-6 text-red-200 mr-3" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-100">
                      {trialStatus.isFreePlan ? 'Free Plan Trial Expired' : 'Trial Period Expired'}
                    </h3>
                    <p className="text-red-200">
                      {trialStatus.isFreePlan 
                        ? 'Your 1-month free plan trial has ended. Please select a paid plan to continue using TeamGrid.'
                        : 'Your 7-day trial has ended. Please select a plan to continue using TeamGrid.'}
                    </p>
                    {trialStatus.trialEnd && (
                      <p className="text-red-200 text-sm mt-1">
                        Trial ended on: {new Date(trialStatus.trialEnd).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-blue-600 border border-blue-500 rounded-lg p-6">
                <div className="flex items-center">
                  <Clock className="h-6 w-6 text-blue-200 mr-3" />
                  <div>
                    <h3 className="text-lg font-semibold text-blue-100">
                      {trialStatus.isFreePlan ? 'Free Plan Trial Active' : 'Trial Period Active'}
                    </h3>
                    <p className="text-blue-200">
                      {trialStatus.daysRemaining} {trialStatus.daysRemaining === 1 ? 'day' : 'days'} remaining in your {trialStatus.isFreePlan ? '1-month free plan' : ''} trial. Select a plan to continue after trial ends.
                    </p>
                    {trialStatus.trialEnd && (
                      <p className="text-blue-200 text-sm mt-1">
                        Trial ends on: {new Date(trialStatus.trialEnd).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Organization Info */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Choose Your Plan</h2>
          <p className="text-gray-400">
            Select the perfect plan for {organization.name}
          </p>
        </div>

        {/* Billing Interval Toggle */}
        <div className="mb-8 flex justify-center">
          <div className="bg-gray-800 rounded-lg p-1 flex flex-wrap justify-center gap-1">
            {intervalOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setBillingInterval(opt.key)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingInterval === opt.key
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-gray-800 rounded-lg p-6 border-2 transition-all cursor-pointer ${
                selectedPlan?.id === plan.id
                  ? 'border-blue-500 bg-gray-750'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
              onClick={() => handlePlanSelection(plan)}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-600 text-white text-sm px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold">{symbol}{plan.price}</span>
                  <span className="text-gray-400">
                    {billingInterval === 'monthly'
                      ? '/month'
                      : ` every ${getIntervalMonths(billingInterval)} months`}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">per user</p>
                <p className="text-gray-400 text-sm mt-2">{plan.description}</p>
              </div>

              {/* Plan Features Tooltip */}
              <div className="mb-6 text-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTooltip(plan.id);
                  }}
                  className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <HelpCircle className="h-4 w-4 mr-1" />
                  <span className="text-sm">View Features</span>
                </button>
                
                {/* Tooltip */}
                {showTooltip === plan.id && (
                  <div className="absolute z-10 mt-2 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-lg p-4">
                    <div className="text-sm">
                      <h4 className="font-semibold text-white mb-2">Plan Features:</h4>
                      <ul className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-gray-300">
                            <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* Arrow */}
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                  </div>
                )}
              </div>

              <button
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  selectedPlan?.id === plan.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {selectedPlan?.id === plan.id ? 'Selected' : 'Select Plan'}
              </button>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          {planConfirmed ? (
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium flex items-center transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              ) : (
                <CreditCard className="h-5 w-5 mr-2" />
              )}
              {loading ? 'Processing...' : 'Add Payment & Start Trial'}
              <ArrowRight className="h-5 w-5 ml-2" />
            </button>
          ) : (
            <button
              onClick={handleChangePlan}
              disabled={!selectedPlan}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium flex items-center transition-colors"
            >
              <Edit className="h-5 w-5 mr-2" />
              Change Plan
              <ArrowRight className="h-5 w-5 ml-2" />
            </button>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center text-gray-400 text-sm">
            <Shield className="h-4 w-4 mr-2" />
            Your payment information is secure and encrypted
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
